import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, ExpressCheckoutElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { api } from "@/lib/api";

function ExpressInner({ product, variant }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [visible, setVisible] = useState(false); // only show once wallets are confirmed available

  const onConfirm = async (event) => {
    if (!stripe || !elements) return;
    setError("");
    try {
      const bd = event.billingDetails || {};
      const addr = bd.address || {};
      const { data } = await api.post("/payments/express/intent", {
        product_id: product.id,
        variant_index: variant,
        quantity: 1,
        email: bd.email || event.email || "",
        name: bd.name || "",
        phone: bd.phone || "",
        address: {
          line1: addr.line1 || "", line2: addr.line2 || "", city: addr.city || "",
          region: addr.state || "", postal_code: addr.postal_code || "", country: addr.country || "",
        },
        origin_url: window.location.origin,
      });
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret: data.client_secret,
        confirmParams: { return_url: `${window.location.origin}/checkout/success` },
        redirect: "if_required",
      });
      if (confirmError) {
        setError(confirmError.message);
        return;
      }
      navigate(`/checkout/success?payment_intent=${paymentIntent.id}`);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Express payment failed");
    }
  };

  return (
    <div data-testid="express-checkout-element">
      {visible && (
        <div className="flex items-center gap-3 mb-3">
          <div className="h-px flex-1 bg-[#2A2522]" />
          <span className="ee-overline text-[10px]">or pay instantly</span>
          <div className="h-px flex-1 bg-[#2A2522]" />
        </div>
      )}
      <ExpressCheckoutElement
        onReady={({ availablePaymentMethods }) => setVisible(!!availablePaymentMethods)}
        onConfirm={onConfirm}
        options={{
          emailRequired: true,
          billingAddressRequired: true,
          buttonHeight: 50,
          paymentMethods: { applePay: "always", googlePay: "always", link: "auto" },
        }}
      />
      {error && <p className="text-red-400 text-xs mt-2" data-testid="express-error">{error}</p>}
    </div>
  );
}

export default function ExpressCheckout({ product, variant, price }) {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    api.get("/payments/express/config").then((r) => setConfig(r.data)).catch(() => setConfig({ enabled: false }));
  }, []);

  const stripePromise = useMemo(
    () => (config?.enabled && config.publishable_key ? loadStripe(config.publishable_key) : null),
    [config],
  );

  if (!config?.enabled || !stripePromise) return null;
  const amount = Math.max(50, Math.round((price || 0) * 100));

  return (
    <div className="mt-4" data-testid="express-checkout">
      <Elements
        stripe={stripePromise}
        options={{
          mode: "payment",
          amount,
          currency: "eur",
          appearance: { theme: "night", variables: { colorPrimary: "#D4AF37", colorBackground: "#0C0A09" } },
        }}
      >
        <ExpressInner product={product} variant={variant} />
      </Elements>
    </div>
  );
}
